import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Palette } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cmsApi, apiError } from "../api";
import { useWebsiteAdmin } from "../context/WebsiteAdminAuth";
import { usePageData, PageTitle, Card, TextField, SelectField, Toggle, SaveBar, Spinner } from "../components/admin-ui";

const slugify = (s) => s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function PasswordCard() {
  const { setToken } = useWebsiteAdmin();
  const [f, setF] = useState({ current_password: "", new_password: "", repeat: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.new_password.length < 8) { toast.error("Neues Passwort: mindestens 8 Zeichen"); return; }
    if (f.new_password !== f.repeat) { toast.error("Passwörter stimmen nicht überein"); return; }
    setBusy(true);
    try { const r = await cmsApi.put("/website-admin/auth/password", { current_password: f.current_password, new_password: f.new_password }); setToken(r.data.token); toast.success("Passwort aktualisiert"); setF({ current_password: "", new_password: "", repeat: "" }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Zugangsdaten Website-Admin" text="Unabhängig vom Loyalty-Admin-Login." testid="password-card">
      <div className="space-y-3">
        <TextField label="Aktuelles Passwort" type="password" value={f.current_password} onChange={(v) => setF({ ...f, current_password: v })} testid="pw-current-input" />
        <TextField label="Neues Passwort" type="password" value={f.new_password} onChange={(v) => setF({ ...f, new_password: v })} testid="pw-new-input" />
        <TextField label="Neues Passwort wiederholen" type="password" value={f.repeat} onChange={(v) => setF({ ...f, repeat: v })} testid="pw-repeat-input" />
        <Button onClick={submit} disabled={busy} variant="outline" className="w-full" data-testid="pw-submit">Passwort ändern</Button>
      </div>
    </Card>
  );
}

export default function SettingsAdmin() {
  const { data, loading } = usePageData("/website-admin/settings");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm((f) => f || data); }, [data]);
  if (loading || !form) return <Spinner />;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setPage = (i, patch) => set("legal_pages")(form.legal_pages.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const save = async () => {
    if (form.legal_pages.some((p) => !p.slug || !p.title)) { toast.error("Jede rechtliche Seite braucht Titel und Slug"); return; }
    setSaving(true);
    try { const { id, updated_at, updated_by, favicon_media_id, primary_color, accent_color, ...body } = form; await cmsApi.put("/website-admin/settings", body); toast.success("Einstellungen gespeichert"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="settings-admin-page">
      <PageTitle title="SEO & Einstellungen" text="SEO, Branding, Footer, rechtliche Seiten und Veröffentlichung." />
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <Card title="SEO">
            <div className="space-y-4">
              <TextField label="Seitentitel (Browser-Tab)" value={form.seo_title} onChange={set("seo_title")} testid="seo-title-input" hint={`${(form.seo_title || "").length}/120 Zeichen`} />
              <TextField label="Meta-Beschreibung" value={form.seo_description} onChange={set("seo_description")} textarea rows={3} testid="seo-description-input" hint={`${(form.seo_description || "").length}/400 Zeichen · empfohlen ≤ 160`} />
              <SelectField label="Sprache" value={form.language} onChange={(v) => set("language")(v || "de")} options={[{ value: "de", label: "Deutsch" }, { value: "en", label: "Englisch" }, { value: "tr", label: "Türkisch" }, { value: "ar", label: "Arabisch" }]} placeholder="Deutsch" testid="language-select" />
            </div>
          </Card>
          <Card title="Rechtliche Seiten" text="Impressum, Datenschutz, AGB … Links erscheinen im Footer.">
            <div className="space-y-4">
              {form.legal_pages.map((p, i) => (
                <div key={i} className="rounded-xl border border-stone-200 p-4 space-y-3" data-testid={`legal-page-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <TextField label="Titel" value={p.title} onChange={(v) => setPage(i, { title: v, slug: p.slug || slugify(v) })} testid={`legal-title-${i}`} />
                    <TextField label="Slug (URL)" value={p.slug} onChange={(v) => setPage(i, { slug: slugify(v) })} testid={`legal-slug-${i}`} hint={`/rechtliches/${p.slug}`} />
                  </div>
                  <TextField label="Inhalt" value={p.content} onChange={(v) => setPage(i, { content: v })} textarea rows={6} testid={`legal-content-${i}`} />
                  <div className="flex items-center justify-between">
                    <Toggle label="Sichtbar" checked={p.visible} onChange={(v) => setPage(i, { visible: v })} testid={`legal-visible-${i}`} />
                    <button onClick={() => set("legal_pages")(form.legal_pages.filter((_, j) => j !== i))} className="inline-flex items-center gap-1 text-xs text-red-700" data-testid={`legal-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /> Seite entfernen</button>
                  </div>
                </div>
              ))}
              <button onClick={() => set("legal_pages")([...form.legal_pages, { slug: "", title: "", content: "", visible: true }])} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A1D24]" data-testid="legal-add"><Plus className="h-4 w-4" /> Seite hinzufügen</button>
            </div>
          </Card>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <Card title="Veröffentlichung">
            <Toggle label="Website öffentlich sichtbar" hint="Deaktiviert zeigt die Website einen Wartungshinweis. Treueprogramm & Admin bleiben erreichbar." checked={form.published} onChange={set("published")} testid="published-toggle" />
            <Toggle label="Link zum Treueprogramm anzeigen" checked={form.show_loyalty_link} onChange={set("show_loyalty_link")} testid="loyalty-link-toggle" />
          </Card>
          <Card title="Branding" text="Logo, Favicon und Markenfarben verwalten Sie unter „Logo & Branding“.">
            <Link to="/website-admin/branding" className="inline-flex items-center gap-2 text-sm font-semibold text-[#4A1D24]" data-testid="settings-branding-link"><Palette className="h-4 w-4" /> Zu Logo & Branding</Link>
          </Card>
          <Card title="Footer"><TextField label="Footer-Text" value={form.footer_text} onChange={set("footer_text")} textarea rows={2} testid="footer-text-input" /></Card>
          <PasswordCard />
        </div>
      </div>
      <SaveBar onSave={save} saving={saving} testid="settings-save" />
    </div>
  );
}
